import { type ChangelogEntry, resolveLocalizedText } from '@app/features/changelog/changelog.utils';
import { getNumberFormattingLocaleByLanguage, normalizeLanguageCode } from '@app/i18n/helpers';
import { Button, Divider, Modal, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

type ChangelogModalProps = {
  entries: ChangelogEntry[];
  mode: 'latest' | 'full';
  onClose: () => void;
  onViewFullHistory?: () => void;
  open: boolean;
};

const formatDate = (date: string, locale: string) => {
  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsedDate);
};

const ChangelogModal = ({ entries, mode, onClose, onViewFullHistory, open }: ChangelogModalProps) => {
  const { i18n, t } = useTranslation();
  const language = normalizeLanguageCode(i18n.language);
  const dateLocale = getNumberFormattingLocaleByLanguage(i18n.language);
  const title = mode === 'latest' ? t('changelog-whats-new') : t('changelog-title');

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={[
        mode === 'latest' && onViewFullHistory ? (
          <Button key="history" onClick={onViewFullHistory}>
            {t('changelog-view-full-btn')}
          </Button>
        ) : null,
        <Button key="close" type="primary" onClick={onClose}>
          {mode === 'latest' ? t('common-done') : t('common-close')}
        </Button>,
      ]}
      width={720}
      title={title}
      destroyOnHidden
    >
      <div className="max-h-[65vh] overflow-y-auto pr-1">
        {entries.map((entry, index) => (
          <div key={`${entry.version}-${entry.date}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Tag color="blue">v{entry.version}</Tag>
              <Typography.Text type="secondary">{formatDate(entry.date, dateLocale)}</Typography.Text>
            </div>

            <Typography.Title level={5} className="!mb-2">
              {resolveLocalizedText(entry.title, language)}
            </Typography.Title>

            <ul className="mb-0 list-disc pl-5">
              {entry.items.map((item) => {
                const text = resolveLocalizedText(item, language);

                return (
                  <li key={`${entry.version}-${text}`} className="mb-1 text-gray-700">
                    {text}
                  </li>
                );
              })}
            </ul>

            {index < entries.length - 1 ? <Divider /> : null}
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default ChangelogModal;
